import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { AnalyticsContext as AnalyticsContextInterface } from '../../../context/analytics';
import {
  SetLegalFirstNameType,
  SetLegalLastNameType,
  SetUserEmailType
} from '../../../store/store';
import Button from '../../../components/UI/Button';

import css from './CreditWaitlistModal.module.scss';
import {
  ModalViewedEvent,
  JoinWaitlistEvent,
  DeclineWaitlistEvent
} from './analytics';
import WaitlistConfirmScreen, {
  WaitlistConfirmScreenProps
} from './WaitlistConfirmScreen';

interface Props {
  email?: SetUserEmailType;
  firstName?: SetLegalFirstNameType;
  lastName?: SetLegalLastNameType;
  vlCampaignId: string;
  showConfirmScreen?: boolean;
  trackEvent: any;
  trackPage: any;
}

const CreditWaitlistModal = ({
  email,
  firstName,
  lastName,
  vlCampaignId,
  showConfirmScreen = true,
  trackEvent,
  trackPage
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] =
    useState<WaitlistConfirmScreenProps>();

  // listen for when viral loops is done loading and then open the waitlist modal
  // https://developers.viral-loops.com/docs/api/ZG9jOjIwNjg2NDQw-popup-widgets#with-a-second-delay
  useEffect(() => {
    window.addEventListener(
      'vl_ready',
      () => {
        setTimeout(() => {
          setShowModal(true);
          trackPage(
            ModalViewedEvent.category,
            ModalViewedEvent.name,
            ModalViewedEvent.properties
          );
        }, 1000);
      },
      false
    );
  }, []);

  const handleJoinWaitlistClick = () => {
    setJoiningWaitlist(true);
    trackEvent(JoinWaitlistEvent.name, {
      eventProps: JoinWaitlistEvent.properties
    });
    // submit the user to viral loops waitlist
    // https://developers.viral-loops.com/docs/api/ZG9jOjIwNjg2NDQw-popup-widgets#create-lead-method
    (window as any).VL.options.form_fields.form_email = email;
    (window as any).VL.options.form_fields.form_firstName = firstName;
    (window as any).VL.options.form_fields.form_lastName = lastName;
    (window as any).VL.createLead(() => {
      if (showConfirmScreen) {
        // show confirmation screen with config data from viral loops
        setWaitlistData({
          rank: (window as any).VL?.options?.lead?.rank,
          shareUrl: (window as any).VL?.options?.form_copy,
          header: (window as any).VL?.options?.textCodes?.invite_prompt,
          description: (window as any).VL?.options?.textCodes?.inviteSubtitle,
          rankLabel: (window as any).VL?.options?.textCodes
            ?.ranking_position_title
        });
        // rank has to be loaded after we join the waitlist
        (window as any).VL.loadRankAndSuggestion(
          (window as any).VL,
          (err, data) => {
            setWaitlistData(prevState => ({ ...prevState, rank: data?.rank }));
          }
        );
      } else {
        setShowModal(false);
      }
    });
  };

  const handleCloseModalClick = () => {
    setShowModal(false);
    if (!waitlistData) {
      trackEvent(DeclineWaitlistEvent.name, {
        eventProps: DeclineWaitlistEvent.properties
      });
    }
  };

  return (
    <>
      <Helmet>
        <script>{`!function(a,b,c,d,t){var e,f=a.getElementsByTagName("head")[0];if(!a.getElementById(c)){if(e=a.createElement(b),e.id=c,e.setAttribute("data-vrlps-ucid",d),e.setAttribute("data-vrlps-version","2"), e.setAttribute("data-vrlps-template", t),e.src="https://app.viral-loops.com/popup_assets/js/vl_load_v2.min.js",window.ub){jQuery=null,$=null;var g=a.createElement(b);g.src="https://code.jquery.com/jquery-2.2.4.min.js",f.appendChild(g)}f.appendChild(e);var h=a.createElement("link");h.rel="stylesheet",h.type="text/css",h.href="https://app.viral-loops.com/static/vl-loader.css",f.appendChild(h);var i=a.createElement("div");i.id="vl-overlay",i.style.display="none";var j=a.createElement("div");j.id="vl-loader",i.appendChild(j),a.addEventListener("DOMContentLoaded",function(b){a.body.appendChild(i);for(var c=a.getElementsByClassName("vrlps-trigger"),d=0;d<c.length;d++)c[d].removeAttribute("href"),c[d].onclick=function(){a.getElementById("vl-overlay").style.display="block"};var e=a.querySelectorAll("[data-vl-widget='popupTrigger']");[].forEach.call(e,function(b){var c=a.createElement("div");c.className="vl-embedded-cta-loader",b.appendChild(c)})})}}(document,"script","vrlps-js","${vlCampaignId}","ranking");`}</script>
      </Helmet>
      {showModal && (
        <div
          className={css.openModal}
          data-testid="credit-waitlist-modal-overlay"
          onClick={handleCloseModalClick}
        >
          <div
            className={css.modal}
            data-testid="credit-waitlist-modal"
            onClick={e => e.stopPropagation()}
          >
            <button
              className={css.closeButton}
              onClick={handleCloseModalClick}
              data-testid="credit-waitlist-modal-close-button"
            >
              <img src="/static/x-icon.svg" alt="Close modal" />
            </button>
            {!waitlistData || !showConfirmScreen ? (
              <>
                <div
                  className={css.introText}
                  data-testid="credit-waitlist-modal-intro"
                >
                  Introducing
                </div>
                <img
                  src={'../../static/parent-credit-card.png'}
                  alt="Greenlight Family Cash Card"
                  className={css.cardImage}
                  data-testid="credit-waitlist-modal-card-image"
                />
                <div
                  className={css.secondaryIntroText}
                  data-testid="credit-waitlist-modal-secondary-intro"
                >
                  The new Greenlight
                </div>
                <h1
                  className={css.title}
                  data-testid="credit-waitlist-modal-title"
                >
                  Family Cash Card
                </h1>
                <p data-testid="credit-waitlist-modal-description">
                  Parents can turn everyday spending into savings with
                  Greenlight&apos;s cash back credit card. Pick to auto-invest
                  or save your rewards.
                </p>
                <h2
                  className={css.prompt}
                  data-testid="credit-waitlist-modal-prompt"
                >
                  Join the waitlist to get exclusive updates and early access
                </h2>
                <Button
                  text="Join waitlist"
                  onClick={handleJoinWaitlistClick}
                  disabled={joiningWaitlist}
                  data-testid="credit-waitlist-modal-join-button"
                />
                <Button
                  text="Not now"
                  outline
                  onClick={handleCloseModalClick}
                  data-testid="credit-waitlist-modal-decline-button"
                />
                <div
                  className={css.footerText}
                  data-testid="credit-waitlist-modal-footer"
                >
                  The Greenlight Family Cash Card is issued by First National
                  Bank of Omaha (FNBO®), pursuant to license by Mastercard
                  International.
                </div>
              </>
            ) : (
              <WaitlistConfirmScreen {...waitlistData} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CreditWaitlistModal;
